import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../../lib/api.js";
import { fmtDateTime } from "../../lib/format.js";
import { Plus, Edit3, Shield, Building2, Layers, Lock, Unlock, Trash2 } from "lucide-react";
import { Badge, Field, Section, Note, inputCls, MODULE_LABELS } from "./_shared.jsx";
import { useAuth } from "../../lib/auth.jsx";

export function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [enums, setEnums] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [actionBusy, setActionBusy] = useState(null); // user_id while mutating

  const load = async () => setUsers((await api.get("/users")).data);

  useEffect(() => {
    load();
    api.get("/enums").then((r) => setEnums(r.data));
  }, []);

  const toggleBlock = async (u) => {
    if (!window.confirm(u.deactivated
      ? `Reactivar ${u.name}? Vai poder entrar novamente na plataforma.`
      : `Bloquear ${u.name}? Deixa de poder entrar até ser reactivado.`)) return;
    setActionBusy(u.id);
    try {
      await api.patch(`/users/${u.id}`, { deactivated: !u.deactivated });
      await load();
    } catch (e) {
      alert(formatError(e.response?.data?.detail) || "Falha a actualizar");
    } finally {
      setActionBusy(null);
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) {
      alert("Não podes apagar a tua própria conta.");
      return;
    }
    if (!window.confirm(`Apagar definitivamente ${u.name} (${u.email})?\n\nIsto NÃO pode ser desfeito. Todo o histórico ligado ao utilizador (SOMs, tarefas, etc.) mantém-se, mas o acesso é removido.`)) return;
    setActionBusy(u.id);
    try {
      await api.delete(`/users/${u.id}`);
      await load();
    } catch (e) {
      alert(formatError(e.response?.data?.detail) || "Falha a apagar");
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#6C757D]">{users.length} utilizadores</div>
        <button onClick={() => setShowNew(true)} className="bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1.5 rounded-sm text-sm flex items-center gap-1" data-testid="new-user-btn">
          <Plus className="w-3 h-3" /> Novo utilizador
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
            <tr>
              <th className="px-3 py-2 text-left">Nome</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Perfil</th>
              <th className="px-3 py-2 text-left">Marcas</th>
              <th className="px-3 py-2 text-left">Filiais</th>
              <th className="px-3 py-2 text-left">Módulos</th>
              <th className="px-3 py-2 text-left">Último login</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-right">Acções</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-gray-100" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2 text-xs">{(u.role || "").replaceAll("_", " ")}</td>
                <td className="px-3 py-2 text-xs">{u.all_brands ? <Badge>Todas</Badge> : (u.assigned_brands?.length ? u.assigned_brands.join(", ") : <span className="text-[#DC3545]">Nenhuma</span>)}</td>
                <td className="px-3 py-2 text-xs">{u.all_branches ? <Badge>Todas</Badge> : (u.assigned_branches?.length ? u.assigned_branches.join(", ") : <span className="text-[#DC3545]">Nenhuma</span>)}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="text-[10px] text-[#6C757D]">{u.effective_modules?.length || 0} módulos</div>
                  {u.assigned_modules?.length > 0 && <Badge tone="amber">+{u.assigned_modules.length} extra</Badge>}
                </td>
                <td className="px-3 py-2 text-xs">{u.last_login ? fmtDateTime(u.last_login) : "—"}</td>
                <td className="px-3 py-2 text-xs">{u.deactivated ? <Badge tone="grey">Bloqueado</Badge> : <Badge tone="green">Activo</Badge>}</td>
                <td className="px-3 py-2 text-xs text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => setEditing(u)} disabled={actionBusy === u.id} className="inline-flex items-center gap-1 text-[#1F3864] hover:bg-slate-100 px-2 py-1 rounded disabled:opacity-50" data-testid={`user-edit-${u.id}`} title="Editar">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => toggleBlock(u)} disabled={actionBusy === u.id || u.id === me?.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-30 ${u.deactivated ? "text-emerald-700" : "text-amber-700"}`} data-testid={`user-toggle-block-${u.id}`} title={u.deactivated ? "Reactivar" : "Bloquear"}>
                      {u.deactivated ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => remove(u)} disabled={actionBusy === u.id || u.id === me?.id} className="inline-flex items-center gap-1 text-rose-600 hover:bg-rose-50 px-2 py-1 rounded disabled:opacity-30" data-testid={`user-delete-${u.id}`} title={u.id === me?.id ? "Não podes apagar a tua conta" : "Apagar"}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && enums && (
        <UserDialog enums={enums} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />
      )}
      {editing && enums && (
        <UserDialog enums={enums} user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}


export function UserDialog({ enums, user, onClose, onSaved }) {
  const isEdit = !!user;
  const defaultRoleModules = useMemo(() => enums.default_role_modules || {}, [enums]);
  const brandUnrestrictedRoles = enums.brand_unrestricted_roles || [];
  const branchUnrestrictedRoles = enums.branch_unrestricted_roles || [];

  const [form, setForm] = useState(() => ({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role || "service_advisor",
    branch: user?.branch || enums.branches[0],
    department: user?.department || "Aftersales",
    deactivated: !!user?.deactivated,
    password: "",
    assigned_brands: user?.assigned_brands || [],
    assigned_branches: user?.assigned_branches || [],
    assigned_modules: user?.assigned_modules || [],
    all_brands: user?.all_brands ?? false,
    all_branches: user?.all_branches ?? false,
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const roleIsBrandUnrestricted = brandUnrestrictedRoles.includes(form.role);
  const roleIsBranchUnrestricted = branchUnrestrictedRoles.includes(form.role);

  // When role changes, adjust scope defaults
  const handleRoleChange = (newRole) => {
    setForm((f) => ({
      ...f,
      role: newRole,
      all_brands: brandUnrestrictedRoles.includes(newRole),
      all_branches: branchUnrestrictedRoles.includes(newRole),
      // Don't clobber explicit brand/branch list, but if becoming unrestricted, all_X flag overrides
    }));
  };

  const defaultModulesForRole = defaultRoleModules[form.role] || [];
  const allModules = enums.modules || [];
  const grantableModules = allModules.filter((m) => !defaultModulesForRole.includes(m));

  const toggle = (key, value) => {
    setForm((f) => {
      const set = new Set(f[key]);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...f, [key]: Array.from(set) };
    });
  };

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
        branch: form.branch,
        department: form.department,
        deactivated: form.deactivated,
        assigned_brands: form.all_brands ? enums.brands : form.assigned_brands,
        assigned_branches: form.all_branches ? enums.branches : form.assigned_branches,
        assigned_modules: form.assigned_modules,
        all_brands: form.all_brands,
        all_branches: form.all_branches,
      };
      if (form.password) payload.password = form.password;
      if (!isEdit) {
        payload.email = form.email;
        await api.post("/users", payload);
      } else {
        await api.patch(`/users/${user.id}`, payload);
      }
      onSaved();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-dialog">
        <div className="sticky top-0 z-10 bg-white px-5 py-3 border-b border-gray-200">
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{isEdit ? "Editar utilizador" : "Novo utilizador"}</div>
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">{form.name || form.email || "Sem nome"}</h2>
        </div>

        <div className="p-5 space-y-5">
          {/* Identity */}
          <Section title="Perfil" icon={Shield}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome completo *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="user-name" /></Field>
              <Field label="Email *">
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} disabled={isEdit} data-testid="user-email" />
              </Field>
              <Field label="Papel">
                <select value={form.role} onChange={(e) => handleRoleChange(e.target.value)} className={inputCls} data-testid="user-role">
                  {enums.roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Field>
              <Field label="Departamento">
                <input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className={inputCls} />
              </Field>
              <Field label={isEdit ? "Nova password (deixa vazio para manter)" : "Password"}>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder={isEdit ? "Deixa vazio" : "Ronil@2026"} data-testid="user-password" />
              </Field>
              <Field label="Estado">
                <select value={form.deactivated ? "inactive" : "active"} onChange={(e) => setForm({ ...form, deactivated: e.target.value === "inactive" })} className={inputCls}>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* Brand access */}
          <Section title="Acesso a marcas" icon={Building2}>
            {roleIsBrandUnrestricted ? (
              <Note tone="info">Este perfil vê sempre todas as marcas.</Note>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm mb-2">
                  <input type="checkbox" checked={form.all_brands} onChange={(e) => setForm({ ...form, all_brands: e.target.checked })} data-testid="user-all-brands" />
                  Dar acesso a <b>todas as marcas</b>
                </label>
                {!form.all_brands && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Marcas atribuídas</div>
                    <div className="flex flex-wrap gap-2">
                      {enums.brands.map((b) => {
                        const on = form.assigned_brands.includes(b);
                        return (
                          <button
                            key={b}
                            onClick={() => toggle("assigned_brands", b)}
                            data-testid={`user-brand-${b}`}
                            className={`px-2.5 py-1 rounded-sm border text-xs font-medium transition-colors ${on ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300 text-[#1A1A2E] hover:border-[#1F3864]"}`}
                          >
                            {b}
                          </button>
                        );
                      })}
                    </div>
                    {form.assigned_brands.length === 0 && (
                      <Note tone="warn">Sem marcas atribuídas — este utilizador não verá <i>nenhuma</i> obra, PEC ou tarefa.</Note>
                    )}
                  </>
                )}
              </>
            )}
          </Section>

          {/* Branch access */}
          <Section title="Acesso a filiais" icon={Building2}>
            {roleIsBranchUnrestricted ? (
              <Note tone="info">Este perfil vê sempre todas as filiais.</Note>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm mb-2">
                  <input type="checkbox" checked={form.all_branches} onChange={(e) => setForm({ ...form, all_branches: e.target.checked })} data-testid="user-all-branches" />
                  Dar acesso a <b>todas as filiais</b>
                </label>
                {!form.all_branches && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Filiais atribuídas</div>
                    <div className="flex flex-wrap gap-2">
                      {enums.branches.map((b) => {
                        const on = form.assigned_branches.includes(b);
                        return (
                          <button
                            key={b}
                            onClick={() => toggle("assigned_branches", b)}
                            data-testid={`user-branch-${b}`}
                            className={`px-2.5 py-1 rounded-sm border text-xs font-medium transition-colors ${on ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300 text-[#1A1A2E] hover:border-[#1F3864]"}`}
                          >
                            {b}
                          </button>
                        );
                      })}
                    </div>
                    {form.assigned_branches.length === 0 && (
                      <Note tone="warn">Sem filiais atribuídas — este utilizador não verá <i>nenhum</i> registo.</Note>
                    )}
                  </>
                )}
              </>
            )}
          </Section>

          {/* Module access */}
          <Section title="Acesso a módulos" icon={Layers}>
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Módulos por defeito do perfil</div>
            <div className="flex flex-wrap gap-1 mb-3">
              {defaultModulesForRole.map((m) => (
                <span key={m} className="px-1.5 py-0.5 rounded-sm bg-[#D4EDDA] text-[#155724] text-[10px] font-semibold uppercase tracking-wider">
                  {MODULE_LABELS[m] || m}
                </span>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Módulos extra concedidos</div>
            <div className="flex flex-wrap gap-2">
              {grantableModules.length === 0 && (
                <span className="text-xs text-[#6C757D]">Este perfil já inclui todos os módulos.</span>
              )}
              {grantableModules.map((m) => {
                const on = form.assigned_modules.includes(m);
                return (
                  <button
                    key={m}
                    onClick={() => toggle("assigned_modules", m)}
                    data-testid={`user-module-${m}`}
                    className={`px-2.5 py-1 rounded-sm border text-xs font-medium transition-colors ${on ? "bg-[#FFC107] text-[#1A1A2E] border-[#FFC107]" : "bg-white border-gray-300 text-[#1A1A2E] hover:border-[#FFC107]"}`}
                  >
                    + {MODULE_LABELS[m] || m}
                  </button>
                );
              })}
            </div>
            {user && (
              <Note tone="info">Todas as alterações de acesso são registadas no audit log com admin, utilizador alvo e valores antigos/novos.</Note>
            )}
          </Section>

          {error && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="user-error">{error}</div>}
        </div>

        <div className="sticky bottom-0 bg-white px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancelar</button>
          <button onClick={submit} disabled={submitting} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60" data-testid="user-submit">
            {submitting ? "A guardar…" : (isEdit ? "Guardar alterações" : "Criar utilizador")}
          </button>
        </div>
      </div>
    </div>
  );
}

